import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Focus trap, Escape to close, body scroll lock, background inert, and focus
 * restoration for modals. Focus lands on `initialFocusRef` (Cancel for
 * destructive dialogs) or the container itself.
 */
export function useModalFocus(onClose: () => void, initialFocusRef?: RefObject<HTMLElement | null>) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const inertTargets: HTMLElement[] = [];
    for (const node of Array.from(document.body.children)) {
      if (!(node instanceof HTMLElement) || node.contains(container) || node.hasAttribute("inert")) continue;
      node.setAttribute("inert", "");
      inertTargets.push(node);
    }

    if (initialFocusRef?.current) initialFocusRef.current.focus();
    else container.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      for (const node of inertTargets) node.removeAttribute("inert");
      if (previouslyFocused && document.body.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }, [onClose, initialFocusRef]);

  return containerRef;
}
